// Basiert auf https://gitlab.pooliestudios.com/koeln-marathon/app/app-dev

import addSeconds from 'date-fns/addSeconds';
import isAfter from 'date-fns/isAfter';
import parseISO from 'date-fns/parseISO';
import {Runner, RunnerInterface} from '../models/runner';
import RunProvider from './run';
import SearchProvider from './search';
import ApiProvider from './api';
import {RUNS} from '../models/waypoint';
import initRunnersStore from '../store/runners';

export interface RunnersDetailResponse {
    nextUpdate?: string;
    data: Array<Runner>;
}

export interface RunnersListResponse {
    nextUpdate?: string;
    data: Array<RunnerInterface>;
}

export default class RunnersProvider {
    static instance = null;

    runners = new Map<number, Map<string, Map<string, Runner>>>();

    nextUpdate = new Map<string, Date>();

    private apiProvider: ApiProvider;

    private searchProvider: SearchProvider;

    private runProvider: RunProvider;

    private runnersStore;

    private subscribeRunnersTimeout;

    static getInstance(): RunnersProvider {
        if (!this.instance) {
            this.instance = new RunnersProvider();
        }

        return this.instance;
    }

    constructor() {
        this.apiProvider = new ApiProvider();
        this.searchProvider = SearchProvider.getInstance();
        this.runProvider = RunProvider.getInstance();
        this.runProvider.getYears()
            .forEach((year) => {
                const runs: Map<string, Map<string, Runner>> = new Map();

                RUNS.forEach((run) => {
                    runs.set(run, new Map());
                });

                this.runners.set(year, runs);
            });
        this.runnersStore = initRunnersStore();
    }

    subscribe(runners: Array<Runner>) {
        if (this.subscribeRunnersTimeout) {
            clearTimeout(this.subscribeRunnersTimeout);
        }

        if (!runners || runners.length === 0) return;
        if (runners.every((runner) => runner.hasFinished())) return;

        this.fetchUpdateRunners(runners)
            .then(() => {
                this.subscribeRunnersTimeout = setTimeout(() => {
                    this.subscribe(runners);
                }, 1000);
            });
    }

    unsubscribe() {
        if (this.subscribeRunnersTimeout) {
            clearTimeout(this.subscribeRunnersTimeout);
        }
    }

    hasRunner(runner: Runner): boolean {
        return this.runners
            .get(runner.year)
            .get(runner.run)
            .has(runner.startNumber);
    }

    private getRunnerFromMap(runner: RunnerInterface): Runner | null {
        if (runner && runner.year && runner.run && runner.startNumber) {
            return this.runners
                .get(runner.year)
                .get(runner.run)
                .get(runner.startNumber);
        }

        return null;
    }

    async getRunners(runners: Array<RunnerInterface>): Promise<Array<Runner>> {
        await this.fetchUpdateRunners(runners);

        return runners
            .map(this.getRunnerFromMap)
            .filter(Boolean);
    }

    setNextUpdateForRunnerInterfaces(runners: Array<Runner>, nextUpdate: Date) {
        runners.forEach((runner) => {
            this.nextUpdate.set(Runner.getUuid(runner), nextUpdate);
        });
    }

    async fetchUpdateRunners(runners: Array<RunnerInterface>): Promise<void> {
        const years = {};
        runners.forEach((runner) => {
            if (!years[runner.year]) {
                years[runner.year] = [];
            }

            years[runner.year].push(runner);
        });

        await Promise.all(
            Object.keys(years)
                .map((year) => this.fetchUpdateRunnersSameYear(years[year])),
        );
    }

    static getNextUpdateFromResult(result: {nextUpdate?: string}): Date {
        const oneMinuteFromNow: Date = addSeconds(new Date(), 60);

        if (result.nextUpdate) {
            return new Date(
                Math.max.apply(null, [parseISO(result.nextUpdate), oneMinuteFromNow]),
            );
        }

        return oneMinuteFromNow;
    }

    async fetchUpdateRunnersSameYear(runners: Array<Runner>): Promise<void> {
        const {year} = runners[0];

        if (runners.every((runner) => runner.hasFinished())) {
            return;
        }

        const startNumbers = runners.map((runner) => runner.startNumber);
        const nextUpdateRunners = runners.map((runner) => this.nextUpdate.get(Runner.getUuid(runner)));

        // Finde den nächstliegenden nextUpdate Wert aller angefragter Läufer
        const minNextUpdate = nextUpdateRunners.reduce((a, b) => (a < b ? a : b));
        if (nextUpdateRunners.every(Boolean) && isAfter(minNextUpdate, new Date())) {
            // eslint-disable-next-line no-console
            console.log(`🆗 providers/runners: fetchUpdateRunners(${year}, [${startNumbers.toString()}])`);
            return;
        }

        // eslint-disable-next-line no-console
        console.log(`⬇️ providers/runners: fetchUpdateRunners(${year}, [${startNumbers.toString()}])`);

        try {
            this.setNextUpdateForRunnerInterfaces(
                runners,
                addSeconds(new Date(), 60),
            );

            const result = await this.fetchRunnersDetail(
                year,
                runners.map((runner) => runner.startNumber),
            );

            this.setNextUpdateForRunnerInterfaces(runners, RunnersProvider.getNextUpdateFromResult(result));

            const parsedRunners = this.parseRunnersData(result.data);
            this.runnersStore.setFavoriteRunner(parsedRunners[0]);

            // Aktuelle Daten als Backup in der DB ablegen
            result.data.forEach((runnerData) => {
                window.localStorage.setItem(
                    `runners.${runnerData.year}.${runnerData.startNumber}`,
                    JSON.stringify(runnerData),
                );
            });
        } catch {
            runners.forEach((runnerInterface) => {
                // Wenn der Läufer noch nicht existiert versuchen Backup aus DB einzuspielen
                if (!this.hasRunner(runnerInterface)) {
                    const runnerData = window.localStorage.getItem(
                        `runners.${runnerInterface.year}.${runnerInterface.startNumber}`,
                    );
                    this.parseRunnersData([JSON.parse(runnerData)], false);
                }
            });

            // Erneut versuchen in 15 Sekunden
            this.setNextUpdateForRunnerInterfaces(
                runners,
                addSeconds(new Date(), 15),
            );
        }
    }

    async fetchRunnersDetail(year: number, startNumbers: Array<string>) {
        return this.apiProvider.getData<RunnersDetailResponse>(year, `runners/${startNumbers.join(',')}`);
    }

    async fetchRunnersList(year: number): Promise<void> {
        const nextUpdateRunners: Date = this.nextUpdate.get(`${year}-runners`);
        if (nextUpdateRunners && isAfter(nextUpdateRunners, new Date())) {
            // eslint-disable-next-line no-console
            console.log(`🆗 providers/runners: fetchUpdateRunnersList(${year}) is up-to-date`);
            return Promise.resolve();
        }

        // eslint-disable-next-line no-console
        console.log(`⬇️ providers/runners: fetchUpdateRunnersList(${year})`);

        try {
            const result = await this.apiProvider.getData<RunnersListResponse>(year, 'runners');
            if (!result || !Object.values(result).length) {
                return Promise.reject();
            }

            this.nextUpdate.set(`${year}-runners`, RunnersProvider.getNextUpdateFromResult(result));

            this.parseRunnersData(result.data);
            window.localStorage.setItem(`runners.${year}`, JSON.stringify(result.data));
        } catch {
            // Backup laden
            const runnersFromStorage: Array<Runner> = JSON.parse(
                window.localStorage.getItem(`runners.${year}`) ?? '[]',
            );
            const sizeOfCachedRunners: number = Array.from(this.runners.get(year)
                .values())
                .reduce((sum: number, runMap) => sum + runMap.size, 0);

            if (runnersFromStorage && sizeOfCachedRunners < runnersFromStorage.length) {
                // Backup einspielen wenn vorhanden
                this.parseRunnersData(runnersFromStorage, false);
                // Sofort erlauben erneut zu versuchen
                this.nextUpdate.set(`${year}-runners`, new Date());
            } else {
                // Erneut versuchen in 15 Sekunden
                this.nextUpdate.set(`${year}-runners`, addSeconds(new Date(), 15));
            }
        }

        return Promise.resolve();
    }

    parseRunnersData(runners: Array<RunnerInterface>, assignToExisting: boolean = true): Array<Runner> {
        if (!runners) return [];

        return runners
            .filter((runner) => runner && runner.year && runner.startNumber)
            .map((runnerData) => {
                const runner = this.getRunnerFromMap({
                    year: runnerData.year,
                    run: runnerData.run,
                    startNumber: runnerData.startNumber,
                });

                if (runner && assignToExisting) {
                    runner.assign(runnerData);
                    return runner;
                }

                const newRunner = new Runner(runnerData);
                this.runners.get(newRunner.year)
                    .get(newRunner.run)
                    .set(newRunner.startNumber, newRunner);

                this.searchProvider.addRunner(newRunner);
                return newRunner;
            });
    }
}
